import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box, Button, Chip, FormControl, IconButton, InputAdornment, InputLabel,
  MenuItem, Select, Tab, Tabs, Table, TableBody, TableCell, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { EmptyState, FilterBarBox, PALETTE, SectionPaper, StatusChip, StyledTableContainer, StyledTableHead, StyledTableRow, filterSelectSx } from "../components/shared";
import { getAttemptResult } from "../hooks/useSkillTests";
import type { PlatformSubmission, SubmissionKind } from "../utils/platformSubmissions";
import { mergePlatformSubmissions } from "../utils/platformSubmissions";
import SkillTestResultView from "./SkillTestResultView";
import ProblemSubmissionDetailView from "./ProblemSubmissionDetailView";

type Props = {
  problemSubmissions: any[];
  skillTestSubmissions: any[];
};

const PAGE_SIZES = [10, 25, 50];
const KIND_LABELS: Record<SubmissionKind, string> = {
  problem: "Problem",
  "skill-test": "Skill Test",
};

export default function SubmissionsView({ problemSubmissions, skillTestSubmissions }: Props) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterLang, setFilterLang] = useState("All");
  const [filterKind, setFilterKind] = useState<"all" | SubmissionKind>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resultLoading, setResultLoading] = useState(false);
  const [attemptResult, setAttemptResult] = useState<any>(null);
  const [selectedProblemSubmission, setSelectedProblemSubmission] = useState<PlatformSubmission | null>(null);

  const submissions = useMemo(
    () => mergePlatformSubmissions(problemSubmissions, skillTestSubmissions),
    [problemSubmissions, skillTestSubmissions],
  );

  const statusOptions = useMemo(
    () => ["All", ...Array.from(new Set(submissions.map((s) => s.statusLabel)))],
    [submissions],
  );
  const langOptions = useMemo(
    () => ["All", ...Array.from(new Set(submissions.map((s) => s.language).filter(Boolean)))],
    [submissions],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return submissions.filter((submission) => {
      if (filterKind !== "all" && submission.kind !== filterKind) return false;
      if (q && !submission.title.toLowerCase().includes(q)) return false;
      if (filterStatus !== "All" && submission.statusLabel !== filterStatus) return false;
      if (filterLang !== "All" && submission.language !== filterLang) return false;
      return true;
    });
  }, [submissions, search, filterStatus, filterLang, filterKind]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const hasFilters = search || filterStatus !== "All" || filterLang !== "All" || filterKind !== "all";
  const clear = () => {
    setSearch("");
    setFilterStatus("All");
    setFilterLang("All");
    setFilterKind("all");
    setPage(0);
  };

  const openSkillTestResult = async (attemptId: string) => {
    setResultLoading(true);
    try {
      const result = await getAttemptResult(attemptId);
      setAttemptResult(result);
    } finally {
      setResultLoading(false);
    }
  };

  const openSubmission = (submission: PlatformSubmission) => {
    if (submission.kind === "skill-test") {
      openSkillTestResult(submission.id);
      return;
    }
    setSelectedProblemSubmission(submission);
  };

  if (selectedProblemSubmission) {
    return (
      <ProblemSubmissionDetailView
        submission={selectedProblemSubmission}
        onBack={() => setSelectedProblemSubmission(null)}
        backLabel="Back to Submissions"
      />
    );
  }

  if (attemptResult) {
    return (
      <SkillTestResultView
        result={attemptResult}
        testMeta={attemptResult.test ?? attemptResult.testMeta}
        onBack={() => setAttemptResult(null)}
        backLabel="Back to Submissions"
      />
    );
  }

  return (
    <Box>
      <Tabs
        value={filterKind}
        onChange={(_e, value) => { setFilterKind(value); setPage(0); }}
        sx={{ mb: 1.25, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: 12, fontWeight: 600 } }}
      >
        <Tab value="all" label={`All (${submissions.length})`} />
        <Tab value="problem" label={`Problems (${submissions.filter((s) => s.kind === "problem").length})`} />
        <Tab value="skill-test" label={`Skill Tests (${submissions.filter((s) => s.kind === "skill-test").length})`} />
      </Tabs>

      <FilterBarBox sx={{ gap: 1, py: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "primary.main", whiteSpace: "nowrap" }}>Filters</Typography>
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}><ClearIcon sx={{ fontSize: 13 }} /></IconButton>
                </InputAdornment>
              ) : null,
              sx: { fontSize: "12px", borderRadius: "8px", bgcolor: "#fff" },
            },
          }}
          sx={{ flex: "1 1 140px", minWidth: 120, maxWidth: 200, "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark } }}
        />
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: "12px" }}>Status</InputLabel>
          <Select
            label="Status"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            sx={{ ...filterSelectSx, fontSize: "12px" }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: "12px", textTransform: "capitalize" }}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: "12px" }}>Language</InputLabel>
          <Select
            label="Language"
            value={filterLang}
            onChange={(e) => { setFilterLang(e.target.value); setPage(0); }}
            sx={{ ...filterSelectSx, fontSize: "12px" }}
          >
            {langOptions.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: "12px", textTransform: "capitalize" }}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
            {hasFilters ? `${filtered.length} of ${submissions.length}` : `${submissions.length}`} submissions
          </Typography>
          {hasFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clear}
              onClick={clear}
              sx={{ fontSize: "11px", bgcolor: "#fff0f0", color: "#c62828", borderColor: "#ffcdd2", "& .MuiChip-deleteIcon": { color: "#c62828" } }}
              variant="outlined"
            />
          )}
        </Box>
      </FilterBarBox>

      {resultLoading ? (
        <EmptyState message="Loading result..." />
      ) : filtered.length === 0 ? (
        <EmptyState message="No submissions found." />
      ) : (
        <SectionPaper>
          <StyledTableContainer>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 820 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: "10%" }}>Type</TableCell>
                  <TableCell sx={{ width: "28%" }}>Title</TableCell>
                  <TableCell sx={{ width: "14%" }}>Status</TableCell>
                  <TableCell sx={{ width: "10%" }}>Language</TableCell>
                  <TableCell sx={{ width: "14%" }}>Details</TableCell>
                  <TableCell sx={{ width: "12%" }}>Submitted</TableCell>
                  <TableCell align="right" sx={{ width: "12%" }}>Actions</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {paginated.map((submission) => (
                  <StyledTableRow key={`${submission.kind}-${submission.id}`}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={KIND_LABELS[submission.kind]}
                        sx={{
                          fontSize: 10,
                          height: 20,
                          bgcolor: submission.kind === "problem" ? "#eff6ff" : "#f5f3ff",
                          color: submission.kind === "problem" ? "#1d4ed8" : "#6d28d9",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={submission.title}>
                        <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "primary.main", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {submission.title}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {submission.kind === "problem" ? (
                        <StatusChip status={submission.status} />
                      ) : (
                        <Chip
                          size="small"
                          label={submission.passed ? "Passed" : "Failed"}
                          sx={{
                            fontSize: 10,
                            height: 20,
                            bgcolor: submission.passed ? "#f0fdf4" : "#fef2f2",
                            color: submission.passed ? "#16a34a" : "#dc2626",
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "12px", textTransform: "capitalize" }}>{submission.language ?? "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      {submission.kind === "problem" ? (
                        <Typography sx={{ fontSize: "12px" }}>
                          {submission.passedTestCases}/{submission.totalTestCases} tests · +{submission.xpEarned ?? 0} XP
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: "12px" }}>
                          {submission.score ?? 0}/{submission.totalMarks ?? 0} marks
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                        {submission.submittedAt ? new Date(submission.submittedAt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        onClick={() => openSubmission(submission)}
                        sx={{ textTransform: "none", fontSize: 11, height: 28, borderRadius: "8px" }}
                      >
                        {submission.kind === "skill-test" ? "View Report" : "View"}
                      </Button>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZES}
            onPageChange={(_e, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ borderTop: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bgLight, "& .MuiTablePagination-toolbar": { minHeight: "40px" }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "12px" } }}
          />
        </SectionPaper>
      )}
    </Box>
  );
}
